import { c as _c } from "react/compiler-runtime";
import { type AddFactiii, type FactiiiTag, instantOfWallClock, type SpaceTimeTag } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { checkTagRequirementsMet } from '@shared/all/validators/checkTagRequirementsMet';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type Dispatch, type SetStateAction } from 'react';
import superjson from 'superjson';
import { convertMyFactiiiTagsDataToFactiiiTag } from '../helpers/formatNumbers';
import toast from '../helpers/toast';
const convertSpaceTimes = (spaceTimeTags: SpaceTimeTag[]): AddFactiii['spaceTimes'] => {
  return spaceTimeTags.map(tag => ({
    timestamp: tag.timestamp,
    latitude: tag.latitude,
    longitude: tag.longitude,
    altitude: tag.altitude
  }));
};
const regex = /^[-+]?([1-8]?\d(\.\d*)?|90(\.0*)?)\s*,\s*[-+]?((1[0-7]\d|([1-9]?\d))(\.\d*)?|180(\.0*)?)$/;

// Coerce spaceTimes to the strict shape the FactiiiTag postFactiiis expects
// (id present, dates parsed, nullable coords). Shared by the location handlers.
const normalizeSpaceTimes = (spaceTimes: SpaceTimeTag[]) => spaceTimes.map(st => ({
  ...st,
  id: st.id ?? 0,
  timestamp: typeof st.timestamp === 'string' ? new Date(st.timestamp) : st.timestamp ?? null,
  latitude: st.latitude ?? null,
  longitude: st.longitude ?? null,
  display: st.display ?? null,
  altitude: st.altitude ?? null,
  deviceVerifiedAt: st.deviceVerifiedAt ?? null
}));
export const useFactiiiHandlers = () => {
  const $ = _c(7);
  const queryClient = useQueryClient();
  const checkRequirementsMet = _temp2;
  const addFactiiis = _temp4;
  let t0;
  if ($[0] !== queryClient) {
    t0 = (tag_1, queryKey) => {
      const myFactiiiTagsData = queryClient.getQueryData(queryKey);
      if (!myFactiiiTagsData) {
        throw new Error("My factiii tags data not found");
      }
      const initialTag = myFactiiiTagsData.find(f => f.factiiiId === tag_1.factiiiId);
      if (!initialTag) {
        throw new Error("Initial tag not found");
      }
      return superjson.stringify(tag_1) !== superjson.stringify(convertMyFactiiiTagsDataToFactiiiTag(initialTag));
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isEdited = t0;
  let t1;
  if ($[2] !== isEdited) {
    t1 = (myPostFactiiiCounts_1, queryKey_0) => {
      const editedFactiiiCounts = myPostFactiiiCounts_1.filter(_temp5).filter(factiii_0 => isEdited(factiii_0, queryKey_0)).map(_temp9);
      const newAddFactiiis = addFactiiis(myPostFactiiiCounts_1.filter(_temp10)).map(_temp14);
      const removeFactiiiIds = myPostFactiiiCounts_1.filter(_temp15).map(_temp16);
      return {
        addFactiiis: newAddFactiiis.concat(editedFactiiiCounts),
        removeFactiiiIds
      };
    };
    $[2] = isEdited;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const remappedAddFactiiis = t1;
  const locationChangeHandler = _temp19;
  const addSpaceTime = _temp21;
  const removeSpaceTime = _temp22;
  const validateDateTime = _temp23;
  const validateLocation = _temp24;
  const dateChangeHandler = _temp26;
  const addFactiii = _temp27;
  let t2;
  if ($[4] !== isEdited || $[5] !== remappedAddFactiiis) {
    t2 = {
      isEdited,
      locationChangeHandler,
      addSpaceTime,
      removeSpaceTime,
      dateChangeHandler,
      addFactiiis,
      validateDateTime,
      validateLocation,
      remappedAddFactiiis,
      checkRequirementsMet,
      addFactiii
    };
    $[4] = isEdited;
    $[5] = remappedAddFactiiis;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
function _temp(tag) {
  return !tag.isRemovedFactiii;
}
function _temp2(post, myPostFactiiiCounts) {
  return myPostFactiiiCounts.filter(_temp).map(tag_0 => ({
    tag: tag_0,
    results: checkTagRequirementsMet(tag_0, post)
  }));
}
function _temp3(factiii) {
  return factiii.isNewFactiii;
}
function _temp4(myPostFactiiiCounts_0) {
  const newFactiiis = myPostFactiiiCounts_0.filter(_temp3);
  return newFactiiis;
}
function _temp5(tag_2) {
  return !tag_2.isNewFactiii && !tag_2.isRemovedFactiii;
}
function _temp6(u) {
  return {
    id: u.id
  };
}
function _temp7(u_0) {
  return {
    id: u_0.id
  };
}
function _temp8(factiii_1) {
  return {
    data: factiii_1.data,
    spaceTimes: factiii_1.spaceTimes,
    uploads: factiii_1.uploads?.map(_temp7),
    factiiiId: factiii_1.factiii.id
  };
}
function _temp9(ft) {
  return {
    factiiiId: ft.factiiiId,
    data: ft.postFactiiis[0].data,
    number: ft.postFactiiis[0].number,
    spaceTimes: convertSpaceTimes(ft.postFactiiis[0].spaceTimes || []),
    uploads: ft.postFactiiis[0].uploads?.map(_temp6),
    references: ft.postFactiiis[0].references,
    factiiiReferences: ft.postFactiiis[0].factiiiReferences?.map(_temp8)
  };
}
function _temp10(factiii_2) {
  return factiii_2.isNewFactiii;
}
function _temp11(u_1) {
  return {
    id: u_1.id
  };
}
function _temp12(u_2) {
  return {
    id: u_2.id
  };
}
function _temp13(factiii_3) {
  return {
    data: factiii_3.data,
    spaceTimes: factiii_3.spaceTimes,
    uploads: factiii_3.uploads?.map(_temp12),
    factiiiId: factiii_3.factiii.id
  };
}
function _temp14(ft_0) {
  return {
    factiiiId: ft_0.factiiiId,
    data: ft_0.postFactiiis[0].data,
    number: ft_0.postFactiiis[0].number,
    spaceTimes: convertSpaceTimes(ft_0.postFactiiis[0].spaceTimes || []),
    uploads: ft_0.postFactiiis[0].uploads?.map(_temp11),
    references: ft_0.postFactiiis[0].references,
    factiiiReferences: ft_0.postFactiiis[0].factiiiReferences?.map(_temp13)
  };
}
function _temp15(factiii_4) {
  return factiii_4.isRemovedFactiii;
}
function _temp16(factiii_5) {
  return factiii_5.factiiiId;
}
function _temp17(coord) {
  return coord.trim();
}
function _temp18(st) {
  return {
    ...st,
    id: st.id ?? 0,
    timestamp: typeof st.timestamp === "string" ? new Date(st.timestamp) : st.timestamp ?? null,
    latitude: st.latitude ?? null,
    longitude: st.longitude ?? null,
    display: st.display ?? null,
    altitude: st.altitude ?? null,
    deviceVerifiedAt: st.deviceVerifiedAt ?? null
  };
}
function _temp19(t0) {
  const {
    tag: tag_3,
    value,
    deviceReceipt,
    setMyPostFactiiiCounts
  } = t0;
  const coordinates = value.split(",").map(_temp17);
  const latitude = coordinates[0];
  const longitude = coordinates[1];
  const isValidLocation = coordinates.length === 2 && !isNaN(Number(latitude)) && !isNaN(Number(longitude)) && regex.test(value);
  const updatedSpaceTimes = tag_3.postFactiiis[0].spaceTimes.length > 0 ? tag_3.postFactiiis[0].spaceTimes.map((spaceTime, index) => index === 0 ? {
    ...spaceTime,
    locationValue: value,
    locationError: !isValidLocation,
    latitude: isValidLocation ? Number(coordinates[0]) : null,
    longitude: isValidLocation ? Number(coordinates[1]) : null,
    deviceReceipt,
    deviceVerifiedAt: null
  } : spaceTime) : [{
    id: 0,
    locationValue: value,
    timestamp: null,
    latitude: isValidLocation ? Number(coordinates[0]) : null,
    longitude: isValidLocation ? Number(coordinates[1]) : null,
    display: null,
    altitude: null,
    locationError: !isValidLocation,
    deviceReceipt
  }];
  const updatedTag = {
    ...tag_3,
    postFactiiis: [{
      ...tag_3.postFactiiis[0],
      spaceTimes: updatedSpaceTimes.map(_temp18)
    }]
  };
  setMyPostFactiiiCounts(prev => prev.map(f_0 => f_0.factiiiId === tag_3.factiiiId ? updatedTag : f_0));
}
function _temp20(c) {
  return c.trim();
}
function _temp21(t0) {
  const {
    tag: tag_4,
    location,
    date,
    time,
    timezone,
    setMyPostFactiiiCounts: setMyPostFactiiiCounts_0
  } = t0;
  let latitude_0 = null;
  let longitude_0 = null;
  let locationValue;
  if (location && location.trim()) {
    const coords = location.split(",").map(_temp20);
    const valid = coords.length === 2 && !isNaN(Number(coords[0])) && !isNaN(Number(coords[1])) && regex.test(location);
    if (!valid) {
      return false;
    }
    latitude_0 = Number(coords[0]);
    longitude_0 = Number(coords[1]);
    locationValue = location;
  }
  let timestamp = null;
  if (date || time || timezone) {
    const isValidDate = date ? !isNaN(Date.parse(date)) : false;
    const isValidTime = time ? /^([01]\d|2[0-3]):([0-5]\d)$/.test(time) : false;
    if (!isValidDate || !isValidTime || !timezone?.value) {
      return false;
    }
    timestamp = instantOfWallClock(date, time, timezone.offset);
  }
  if (latitude_0 === null && timestamp === null) {
    return false;
  }
  const newSpaceTime = {
    id: 0,
    locationValue,
    timestamp,
    latitude: latitude_0,
    longitude: longitude_0,
    display: null,
    altitude: null,
    date,
    time,
    timezone,
    locationError: false
  };
  const updatedTag_0 = {
    ...tag_4,
    postFactiiis: [{
      ...tag_4.postFactiiis[0],
      spaceTimes: normalizeSpaceTimes([...tag_4.postFactiiis[0].spaceTimes, newSpaceTime])
    }, ...tag_4.postFactiiis.slice(1)]
  };
  setMyPostFactiiiCounts_0(prev_0 => prev_0.map(f_1 => f_1.factiiiId === tag_4.factiiiId ? updatedTag_0 : f_1));
  return true;
}
function _temp22(t0) {
  const {
    tag: tag_5,
    index: index_0,
    setMyPostFactiiiCounts: setMyPostFactiiiCounts_1
  } = t0;
  const updatedTag_1 = {
    ...tag_5,
    postFactiiis: [{
      ...tag_5.postFactiiis[0],
      spaceTimes: normalizeSpaceTimes(tag_5.postFactiiis[0].spaceTimes.filter((_, i) => i !== index_0))
    }, ...tag_5.postFactiiis.slice(1)]
  };
  setMyPostFactiiiCounts_1(prev_1 => prev_1.map(f_2 => f_2.factiiiId === tag_5.factiiiId ? updatedTag_1 : f_2));
}
function _temp23(date_0, time_0, timezone_0) {
  const isClearAction = !date_0 && !time_0;
  if (isClearAction) {
    return true;
  } else {
    const isValidDate_0 = date_0 ? !isNaN(Date.parse(date_0)) : false;
    const isValidTime_0 = time_0 ? /^([01]\d|2[0-3]):([0-5]\d)$/.test(time_0) : false;
    const isValidTimezone = Boolean(timezone_0?.value);
    return isValidDate_0 && isValidTime_0 && isValidTimezone;
  }
}
function _temp24(value_0) {
  if (!value_0.trim()) {
    return true;
  }
  const coords_0 = value_0.split(",");
  if (coords_0.length !== 2) {
    return false;
  }
  const lat = parseFloat(coords_0[0].trim());
  const lng = parseFloat(coords_0[1].trim());
  return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}
function _temp25(st_0) {
  return {
    ...st_0,
    id: st_0.id ?? 0,
    timestamp: typeof st_0.timestamp === "string" ? new Date(st_0.timestamp) : st_0.timestamp ?? null,
    latitude: st_0.latitude ?? null,
    longitude: st_0.longitude ?? null,
    display: st_0.display ?? null,
    altitude: st_0.altitude ?? null,
    deviceVerifiedAt: st_0.deviceVerifiedAt ?? null
  };
}
function _temp26(t0) {
  const {
    tag: tag_6,
    date: date_1,
    time: time_1,
    timezone: timezone_1,
    setMyPostFactiiiCounts: setMyPostFactiiiCounts_2
  } = t0;
  const isClearAction_0 = !date_1 && !time_1 && !timezone_1;
  let timestamp_0;
  let dateTimeError = false;
  if (isClearAction_0) {
    timestamp_0 = undefined;
    dateTimeError = false;
  } else {
    const isValidDate_1 = date_1 ? !isNaN(Date.parse(date_1)) : false;
    const isValidTime_1 = time_1 ? /^([01]\d|2[0-3]):([0-5]\d)$/.test(time_1) : false;
    const isValidTimezone_0 = Boolean(timezone_1?.value);
    if (isValidDate_1 && isValidTime_1 && isValidTimezone_0 && date_1 && time_1 && timezone_1) {
      ;
      try {
        timestamp_0 = instantOfWallClock(date_1, time_1, timezone_1.offset).toISOString();
      } catch (t1) {
        const error = t1;
        console.error("Failed to construct timestamp:", error);
        dateTimeError = true;
      }
    } else {
      dateTimeError = true;
    }
  }
  const updatedSpaceTimes_0 = tag_6.postFactiiis[0].spaceTimes && tag_6.postFactiiis[0].spaceTimes.length > 0 ? tag_6.postFactiiis[0].spaceTimes.map((spaceTime_0, index_1) => index_1 === 0 ? {
    ...spaceTime_0,
    dateTimeError,
    date: isClearAction_0 ? undefined : date_1 || (spaceTime_0 as SpaceTimeTag).date,
    time: isClearAction_0 ? undefined : time_1 || (spaceTime_0 as SpaceTimeTag).time,
    timezone: isClearAction_0 ? undefined : timezone_1 || (spaceTime_0 as SpaceTimeTag).timezone,
    timestamp: isClearAction_0 ? undefined : timestamp_0
  } : spaceTime_0) : [{
    id: 0,
    timestamp: isClearAction_0 ? null : typeof timestamp_0 === "string" ? new Date(timestamp_0) : timestamp_0 as Date | null,
    latitude: null,
    longitude: null,
    display: null,
    altitude: null,
    dateTimeError,
    date: isClearAction_0 ? undefined : date_1,
    time: isClearAction_0 ? undefined : time_1,
    timezone: isClearAction_0 ? undefined : timezone_1
  }];
  const updatedTag_2 = {
    ...tag_6,
    postFactiiis: [{
      ...tag_6.postFactiiis[0],
      spaceTimes: updatedSpaceTimes_0.map(_temp25)
    }]
  };
  setMyPostFactiiiCounts_2(prev_2 => prev_2.map(f_3 => f_3.factiiiId === tag_6.factiiiId ? updatedTag_2 : f_3));
}
function _temp27(t0) {
  const {
    tag: tag_7,
    setMyPostFactiiiCounts: setMyPostFactiiiCounts_3,
    setPolitics,
    setNsfw,
    myPostFactiiiCounts: myPostFactiiiCounts_2
  } = t0;
  if (tag_7.isRetired) {
    toast("This factiii is retired", "error");
    return;
  }
  if (tag_7.name === "Politics") {
    setPolitics(true);
  }
  if (tag_7.name === "NSFW") {
    setNsfw(true);
  }
  if (myPostFactiiiCounts_2.some(f_4 => f_4.factiiiId === tag_7.factiiiId)) {
    toast("You already have this factiii selected");
  } else {
    setMyPostFactiiiCounts_3(prev_3 => [...prev_3, {
      ...tag_7,
      isNewFactiii: true
    }]);
  }
}